import apiClient from "../api/axios";
import alertService from "../components/alert/alertService";
import { handleApiError } from "../api/handleApiError";
import {
  GetTemplateResponse,
  ValidateFileResponse,
  ImportActivitiesHistoryForDataTableResponse,
  FileDetail,
  ImportActivitiesHistoryForDataTable,
  ImportedActivity,
  ValidationResult,
} from "../pages/import-data/ImportFilePopup.type";
import { ApiResponse } from "../models/api-responses.types";
import { DROPDOWN_EMISSION_GROUP } from "../components/emission/emission-components";

const ImportFileService = {
  getTemplate: async (): Promise<GetTemplateResponse | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.get(`/v1/activities/import/template`);
      return response.data.results;
    } catch (error) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  validateFile: async (
    filename: string,
    excelBase64String: string,
    importHistoryRefId?: string,
  ): Promise<ValidateFileResponse | null> => {
    const body: any = {
      filename: filename,
      excelBase64String: excelBase64String.split("base64,")[1],
    };

    if (importHistoryRefId) {
      body.importHistoryRefId = importHistoryRefId;
    }

    try {
      alertService.showLoading();
      const response = await apiClient.post(
        `/v2/activities/import/validate`,
        body,
      );
      return response.data.results;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  importFile: async (
    organizationId: number,
    organizationRef: string,
    validateFileResponse: ValidateFileResponse,
  ): Promise<ImportActivitiesHistoryForDataTableResponse | null> => {
    const activities = validateFileResponse?.importActivitiesHistory.map(
      (e) => ({
        id: e.id,
        asset: e.asset,
        assetRef: e.assetRef,
        amount: e.amount,
        startDate: e.startDate,
        endDate: e.endDate,
        organization: organizationId,
        organizationRef: organizationRef,
        suborganization: e.suborganization,
        suborganizationRef: e.suborganizationRef,
        emissionFactorRef: e.emissionFactorRef,
        vender: e.vender,
        remark: e.remark,
        auxiliary: e.auxiliary,
        site: e.site,
        siteRef: e.siteRef,
        emissionGroup:
          DROPDOWN_EMISSION_GROUP.find(
            (i) => i.emissionGroup === e.emissionGroup,
          )?._id ?? "",
        evidenceFileName: e.evidenceFileName,
        evidenceS3FileName: e.evidenceS3FileName,
        evidenceS3Url: e.evidenceS3Url,
        evidenceS3UrlExpiresAt: e.evidenceS3UrlExpiresAt,
      }),
    );

    const body = {
      importActivitiesRef: validateFileResponse.importActivitiesRef,
      activities: activities,
    };

    try {
      const response = await apiClient.post(`/v3/activities/import`, body);
      return response.data.results;
    } catch (error) {
      handleError(error);
    }
    return null;
  },
  getImportHistories: async (): Promise<
    ImportActivitiesHistoryForDataTable[] | null
  > => {
    try {
      const response = await apiClient.get(`/v2/activities/import`);
      return response.data.results.histories;
    } catch (error) {
      handleError(error);
    }
    return null;
  },
  deleteImportHistory: async (
    refId: string,
  ): Promise<ImportActivitiesHistoryForDataTableResponse | null> => {
    try {
      const response = await apiClient.patch(`/v2/activities/import/${refId}`);
      return response.data.results;
    } catch (error) {
      handleError(error);
    }
    return null;
  },
  getImportHistoryFile: async (refId: string): Promise<FileDetail | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.get(`/v1/activities/import/${refId}`);
      return response.data.results;
    } catch (error) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },

  getImportHistoryDetail: async (
    refId: string,
  ): Promise<ImportedActivity[] | ValidationResult | null> => {
    try {
      const response = await apiClient.get<
        ApiResponse<
          | {
              numResult: number;
              activities: ImportedActivity[];
            }
          | ValidationResult
        >
      >(`/v2/activities/import/${refId}/detail`);

      const results = response.data.results;

      if (results && "activities" in results) {
        return results.activities;
      }

      return results as ValidationResult;
    } catch (error) {
      handleError(error);
    }
    return null;
  },
};

// the server's codes for refusing a whole workbook, and the message each one shows
export const IMPORT_REFUSAL_MESSAGE_KEYS: Record<string, string> = {
  EXCEL_WORKSHEET_EMPTY: "Excel-worksheet-incompleted",
  EXCEL_WORKSHEET_HAS_HIDDEN_OR_MERGED: "EXCEL-WORKSHEET-HAS-HIDDEN-OR-MERGED",
  EXCEL_SIZE_TOO_LARGE: "File-Size-Exceeded",
  IMPORT_EXCEEDS_ROW_LIMIT: "Import-Exceeds-Row-Limit",
};

const UPLOAD_INCOMPLETE = {
  messageKey: "upload-incomplete-detail",
  titleKey: "upload-incomplete",
};

// a refused workbook is saved as one row-0 error with this column; header errors also use row 0
export const WHOLE_FILE_REFUSAL_COLUMN = "file";

// the message a refused file's history line shows: the one its upload pop-up showed
export const importRefusalMessageKey = (code: string) =>
  IMPORT_REFUSAL_MESSAGE_KEYS[code] ?? UPLOAD_INCOMPLETE.messageKey;

const handleError = (error: unknown) =>
  handleApiError(
    error,
    {
      EXCEL_PARSING_FROM_BASE64_FAILED: { ...UPLOAD_INCOMPLETE, type: "error" },
      ...IMPORT_REFUSAL_MESSAGE_KEYS,
    },
    UPLOAD_INCOMPLETE,
  );

export default ImportFileService;
